import { useLingui } from '@lingui/react/macro'
import { Text } from 'designSystem/components/Text'
import {
  MetadataFieldList,
  type MetadataFieldValueControl,
} from 'features/knowledgeDocumentViewer/components/metadata/MetadataFieldList'
import type { DocumentPageMetadataUserEditValues } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import { withDocumentTypeLabels } from 'features/knowledgeDocumentViewer/utils/documentTypeLabels'
import {
  getPageMetadataDisplayFields,
  hasPageMetadataBeyondPageNumber,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataFields'
import React from 'react'
import styled from 'styled-components'

const PageMetaRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export type PageMetadataContentProps = {
  page: SystemDocumentPageEntry | undefined
  pageNum: number
  isLoading: boolean
  userEditValues?: DocumentPageMetadataUserEditValues | null
  includeInternalIds?: boolean
  valueControl?: MetadataFieldValueControl
}

export function PageMetadataContent({
  page,
  pageNum,
  isLoading,
  userEditValues,
  includeInternalIds = false,
  valueControl,
}: PageMetadataContentProps): React.ReactElement {
  const { t, i18n } = useLingui()
  if (isLoading) {
    return (
      <Text variant='body' color='muted'>
        {t`Loading page metadata…`}
      </Text>
    )
  }

  const fields = withDocumentTypeLabels(
    getPageMetadataDisplayFields(page, pageNum, userEditValues, {
      includeInternalIds,
      keepEmptyLabel: valueControl?.label,
    }),
    (descriptor) => i18n._(descriptor)
  )
  const showEmptyNote = !hasPageMetadataBeyondPageNumber(fields)

  return (
    <PageMetaRoot>
      <MetadataFieldList fields={fields} valueControl={valueControl} />
      {showEmptyNote ? (
        <Text variant='body' color='muted' italic>
          {t`No page metadata for page ${pageNum}`}
        </Text>
      ) : null}
    </PageMetaRoot>
  )
}
